import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Token refresh interval: check every 4 hours
const TOKEN_REFRESH_INTERVAL = 4 * 60 * 60 * 1000; // 4 hours in milliseconds

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenRefreshedAt');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    
    // Clear refresh interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Refresh token function - keeps active users logged in
  const refreshToken = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return;

    // Only refresh if user has been active in the last 12 hours
    const inactiveTime = Date.now() - lastActivityRef.current;
    const maxInactiveTime = 12 * 60 * 60 * 1000; // 12 hours
    
    if (inactiveTime > maxInactiveTime) {
      console.log('User inactive for too long, skipping token refresh');
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/refresh`);
      const { token: newToken, refreshed, token_age_hours } = response.data;
      
      if (refreshed) {
        console.log(`Token refreshed (was ${token_age_hours}h old)`);
        localStorage.setItem('token', newToken);
        localStorage.setItem('tokenRefreshedAt', new Date().toISOString());
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
      } else {
        console.log(`Token still fresh (${token_age_hours}h old), no refresh needed`);
      }
    } catch (error) {
      // Don't log out on refresh failure - the token might still be valid
      console.error('Token refresh failed:', error.message);
    }
  }, []);

  // Set up axios interceptor to handle 401 errors globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - log out the user
          console.log('Session expired - logging out');
          logout();
          // Redirect to login if not already there
          if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
            window.location.href = '/login?expired=true';
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  // Track user activity on interactions
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [updateActivity]);

  // Set up token refresh interval
  useEffect(() => {
    if (token && user) {
      // Refresh token on app startup (only after user is loaded)
      refreshToken();

      // Set up periodic refresh (every 4 hours)
      refreshIntervalRef.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);

      // Also refresh when app becomes visible (user returns to tab)
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          updateActivity();
          refreshToken();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [token, user, refreshToken, updateActivity]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('tokenRefreshedAt', new Date().toISOString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    updateActivity();
    return userData;
  };

  const completeRegistration = async (inviteToken, password) => {
    const response = await axios.post(`${API}/auth/complete-registration`, { 
      invite_token: inviteToken, 
      password 
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('tokenRefreshedAt', new Date().toISOString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    updateActivity();
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, completeRegistration, logout, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};
